import React, { useState, useCallback } from 'react';
import { runCleanse, getCleanseReport, updateForecastConfig } from '../../api/analytics';

const RULE_TYPES = [
  { value: 'impute',  label: 'Impute',  description: 'Fill missing values' },
  { value: 'clip',    label: 'Clip',    description: 'Hard bound numeric values' },
  { value: 'winsor',  label: 'Winsor',  description: 'Percentile-based clipping' },
  { value: 'drop',    label: 'Drop',    description: 'Drop column or matching rows' },
  { value: 'cast',    label: 'Cast',    description: 'Type coercion' },
  { value: 'exclude', label: 'Exclude', description: 'Remove rows matching condition' },
  { value: 'derive',  label: 'Derive',  description: 'Create new feature column' },
];

const IMPUTE_STRATEGIES = ['median', 'mode', 'constant'];
const CAST_TYPES = ['float64', 'int64', 'bool', 'string'];
const DERIVE_FORMULAS = ['log1p', 'abs', 'clip', 'date_diff'];

function RuleEditor({ rule, index, onChange, onRemove, featureSchema }) {
  const update = (field, value) => onChange(index, { ...rule, [field]: value });

  return (
    <div style={{
      border: '1px solid #e0e0e0', borderRadius: 6, padding: 12, marginBottom: 8,
      background: '#fafafa', position: 'relative'
    }}>
      <button
        onClick={() => onRemove(index)}
        style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 16 }}
        title="Remove rule"
      >×</button>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontWeight: 500, minWidth: 20, color: '#666' }}>{index + 1}.</span>

        <select
          value={rule.type || ''}
          onChange={e => update('type', e.target.value)}
          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
          data-testid={`cleanse-rule-${index}-type`}
        >
          <option value="">Select type…</option>
          {RULE_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label} — {t.description}</option>
          ))}
        </select>

        {/* Column picker for rules that need a column */}
        {['impute', 'clip', 'winsor', 'drop', 'cast', 'derive'].includes(rule.type) && (
          <select
            value={rule.column || ''}
            onChange={e => update('column', e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
            data-testid={`cleanse-rule-${index}-column`}
          >
            <option value="">Select column…</option>
            {(featureSchema || []).map(col => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>
        )}

        {/* Impute strategy */}
        {rule.type === 'impute' && (
          <>
            <select
              value={rule.strategy || 'median'}
              onChange={e => update('strategy', e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
              data-testid={`cleanse-rule-${index}-strategy`}
            >
              {IMPUTE_STRATEGIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {rule.strategy === 'constant' && (
              <input
                type="number"
                value={rule.value ?? 0}
                onChange={e => update('value', parseFloat(e.target.value))}
                style={{ width: 80, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
                data-testid={`cleanse-rule-${index}-value`}
              />
            )}
          </>
        )}

        {/* Clip / Winsor bounds */}
        {['clip', 'winsor'].includes(rule.type) && (
          <>
            <input type="number" placeholder="lower" value={rule.lower ?? ''} onChange={e => update('lower', parseFloat(e.target.value))}
              style={{ width: 80, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }} data-testid={`cleanse-rule-${index}-lower`} />
            <input type="number" placeholder="upper" value={rule.upper ?? ''} onChange={e => update('upper', parseFloat(e.target.value))}
              style={{ width: 80, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }} data-testid={`cleanse-rule-${index}-upper`} />
          </>
        )}

        {/* Cast type */}
        {rule.type === 'cast' && (
          <select value={rule.to || 'float64'} onChange={e => update('to', e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }} data-testid={`cleanse-rule-${index}-to`}>
            {CAST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}

        {/* Derive formula */}
        {rule.type === 'derive' && (
          <>
            <select value={rule.formula || ''} onChange={e => update('formula', e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }} data-testid={`cleanse-rule-${index}-formula`}>
              <option value="">Select formula…</option>
              {DERIVE_FORMULAS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <input placeholder="New column name" value={rule.new_column || ''} onChange={e => update('new_column', e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }} data-testid={`cleanse-rule-${index}-new-column`} />
          </>
        )}

        {/* Exclude / Drop condition */}
        {['exclude', 'drop'].includes(rule.type) && (
          <input
            placeholder='condition e.g. amount < 0'
            value={rule.condition || ''}
            onChange={e => update('condition', e.target.value)}
            style={{ flex: 1, minWidth: 180, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
            data-testid={`cleanse-rule-${index}-condition`}
          />
        )}
      </div>
    </div>
  );
}

export default function CleansePhase({ configId, config, featureRuns, onRulesChanged }) {
  const [rules, setRules] = useState(config?.cleanse_rules || []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  // Latest assembled run
  const assembledRun = featureRuns?.find(r => r.status === 'assembled' || r.status === 'trained');
  const featureSchema = assembledRun?.feature_schema || [];

  const handleRuleChange = useCallback((index, updated) => {
    setRules(prev => prev.map((r, i) => i === index ? updated : r));
    setDirty(true);
  }, []);

  const handleRemoveRule = useCallback((index) => {
    setRules(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  }, []);

  const handleAddRule = () => {
    setRules(prev => [...prev, { type: '', column: '' }]);
    setDirty(true);
  };

  const handleSaveRules = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateForecastConfig(configId, { ...config, cleanse_rules: rules });
      setDirty(false);
      if (onRulesChanged) onRulesChanged(rules);
    } catch (e) {
      setError(e.data?.message || e.message || 'Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const handleRunCleanse = async () => {
    if (!assembledRun) { setError('No assembled run — assemble features first'); return; }
    setRunning(true);
    setError(null);
    setReport(null);
    try {
      await runCleanse(configId, assembledRun.id);
      // Poll for completion
      let attempts = 0;
      const poll = async () => {
        try {
          const res = await getCleanseReport(configId);
          setReport(res.data);
          setRunning(false);
        } catch (e) {
          if (e.status === 404 && attempts < 30) {
            attempts++;
            setTimeout(poll, 3000);
          } else {
            setError('Cleanse did not complete. Check run status.');
            setRunning(false);
          }
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      setError(e.data?.message || e.message || 'Failed to start cleanse');
      setRunning(false);
    }
  };

  return (
    <div style={{ padding: 16 }} data-testid="cleanse-phase">
      {/* Stale artifact warning */}
      {dirty && assembledRun?.cleansed_feature_parquet && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: 10, marginBottom: 12, color: '#856404' }}>
          Rules changed — cleansed data is stale. Save rules then re-run cleanse before training.
        </div>
      )}

      {/* Rules list */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong>Cleanse Rules</strong>
          {!featureSchema.length && (
            <span style={{ color: '#888', fontSize: 12 }}>Assemble features first to enable column validation</span>
          )}
        </div>

        {rules.length === 0 && (
          <div style={{ color: '#aaa', padding: '12px 0', textAlign: 'center' }}>
            No rules configured. Training will use raw assembled features.
          </div>
        )}

        {rules.map((rule, i) => (
          <RuleEditor key={i} rule={rule} index={i} onChange={handleRuleChange} onRemove={handleRemoveRule} featureSchema={featureSchema} />
        ))}

        <button onClick={handleAddRule} data-testid="cleanse-add-rule"
          style={{ padding: '6px 14px', borderRadius: 4, border: '1px dashed #aaa', background: 'none', cursor: 'pointer', color: '#555', marginTop: 4 }}>
          + Add Rule
        </button>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={handleSaveRules} disabled={!dirty || saving} data-testid="cleanse-save-rules"
          style={{ padding: '6px 16px', borderRadius: 4, border: '1px solid #1976d2', background: dirty ? '#1976d2' : '#eee', color: dirty ? '#fff' : '#aaa', cursor: dirty ? 'pointer' : 'default' }}>
          {saving ? 'Saving…' : 'Save Rules'}
        </button>

        <button onClick={handleRunCleanse} disabled={running || !assembledRun} data-testid="cleanse-run"
          style={{ padding: '6px 16px', borderRadius: 4, border: '1px solid #388e3c', background: (!running && assembledRun) ? '#388e3c' : '#eee', color: (!running && assembledRun) ? '#fff' : '#aaa', cursor: (!running && assembledRun) ? 'pointer' : 'default' }}>
          {running ? 'Running…' : 'Run Cleanse'}
        </button>
      </div>

      {error && (
        <div style={{ color: '#d32f2f', background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 4, padding: 10, marginBottom: 12 }} data-testid="cleanse-error">
          {error}
        </div>
      )}

      {/* Summary */}
      {report && (
        <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: 12 }} data-testid="cleanse-report">
          <strong>Cleanse Complete</strong>
          <div style={{ marginTop: 6 }}>
            {report.rows_before?.toLocaleString()} rows {'\u2192'} <strong>{report.rows_after?.toLocaleString()} rows</strong>
            {' '}({((report.rows_before - report.rows_after) || 0).toLocaleString()} dropped)
          </div>
          {report.steps?.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: '#2e7d32' }}>Step details ({report.steps.length} rules applied)</summary>
              <ul style={{ marginTop: 6, paddingLeft: 20 }}>
                {report.steps.map((step, i) => (
                  <li key={i} style={{ fontSize: 13, color: '#555' }}>
                    Rule {step.index + 1} ({step.type}{step.column ? ` on ${step.column}` : ''}): {step.rows_before?.toLocaleString()} {'\u2192'} {step.rows_after?.toLocaleString()} rows
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* No assembled run warning */}
      {!assembledRun && (
        <div style={{ color: '#888', fontSize: 13, marginTop: 8 }}>
          No assembled feature run found. Assemble features before running cleanse.
        </div>
      )}
    </div>
  );
}
